import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { NavLink } from 'react-router-dom';
import { Icon } from 'Components/Icons';
import { RocketPlanHeader } from 'Components/RocketPlanHeader';
import { SideBarNav } from './SideBarNav';

import classes from './sideBar.module.css';

type NavItemsType = {
  id: number;
  title: string;
  path: string;
  icon: string;
};

type Company = {
  id: number;
  name: string;
};

interface Props {
  sideBarDesktop: boolean;
  sideBarMobile: boolean;
  companies?: Company[];
  selectedCompanyId: number | null;
  switchingCompany?: boolean;
  onCompanyChange: (companyId: number) => void;
  toggleSideBar: () => void;
  navItems: Array<NavItemsType>;
  pathname: string;
}

const SideBar = ({
  sideBarDesktop,
  sideBarMobile,
  companies,
  selectedCompanyId,
  switchingCompany,
  onCompanyChange,
  toggleSideBar,
  navItems,
  pathname,
}: Props) => {
  const hasCompanies = (companies?.length ?? 0) > 0;
  const hasMultipleCompanies = (companies?.length ?? 0) > 1;

  const handleCompanySelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    onCompanyChange(Number(value));
  };

  return (
    <div
      className={`d-flex flex-column ${sideBarDesktop ? classes?.sideBarClosed : ''} ${
        sideBarMobile ? classes.sideBarMobile : ''
      }`}
    >
      <div className={`${classes.sideBar}`}>
        <Icon type="sideBarClose" className={classes.sideBarCloseIcon} onClick={toggleSideBar} />
        <div className="d-flex flex-column w-100">
          <div className="d-flex flex-column w-100">
            <div className={`${classes.sideBarHeader} ${classes?.sideBarInner}`}>
              <NavLink
                to="/dashboard"
                className={`${sideBarDesktop ? classes.logoIcon : classes.largeLogo}`}
                aria-current="page"
              >
                {sideBarDesktop ? (
                  <Icon className={classes.headerLogoIcon} type="logoIcon" />
                ) : (
                  <span className={classes.headerLogo}>
                    <RocketPlanHeader iconClassName="logo" />
                  </span>
                )}
              </NavLink>
              <div className={classes.hr} />
            </div>
            <div className={`${classes.companyNameContainer}`}>
              {sideBarDesktop ? (
                <Icon type="threeDots" />
              ) : (
                <div className={classes.companySwitcher}>
                  {hasCompanies ? (
                    <>
                      <select
                        id="companySwitcher"
                        className={classes.companySelect}
                        value={selectedCompanyId ?? companies?.[0]?.id ?? ''}
                        onChange={handleCompanySelect}
                        disabled={!hasMultipleCompanies || switchingCompany}
                        aria-label="Switch company"
                      >
                        {[...(companies || [])]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(({ id, name }) => (
                            <option key={id} value={id}>
                              {name}
                            </option>
                          ))}
                      </select>
                      {switchingCompany && <span className={classes.switchingText}>Switching...</span>}
                    </>
                  ) : (
                    <p className={classes.companyPlaceholder}>No companies available</p>
                  )}
                </div>
              )}
            </div>
            <SideBarNav navItems={navItems} minimized={sideBarDesktop} pathname={pathname} />
          </div>
        </div>
      </div>
      <div className={`${classes.iconContainer}`}>
        <Icon
          className={`${classes.sideBarArrow}`}
          type={sideBarDesktop ? 'sideBarRightArrow' : 'sideBarLeftArrow'}
          onClick={toggleSideBar}
        />
      </div>
    </div>
  );
};

SideBar.defaultProps = {
  companies: [],
  switchingCompany: false,
};

const SideBarMemo = memo(SideBar, areEqual);

export { SideBarMemo as SideBar };
